import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MdQrCodeScanner,
  MdPerson,
  MdEmail,
  MdPhone,
  MdVerified,
  MdPending,
  MdArrowBack,
  MdHistory,
  MdRefresh,
  MdCheckCircle,
  MdAccessTime,
  MdEvent,
  MdLibraryMusic
} from 'react-icons/md';
import QRScanner from '../../components/QRScanner';
import { getActivityDetails } from '../../api/activities';
import { formatWIBTime, formatWIBDate, getCurrentWIB } from '../../utils/dateUtils';

// Theme configuration
const THEMES = {
  event: {
    name: 'Event Management',
    primary: 'blue',
    gradient: 'from-blue-600 to-indigo-600',
    background: 'bg-gray-50',
    card: 'bg-white',
    text: 'text-gray-900',
    accent: 'text-blue-600',
    icon: MdEvent
  },
  fan: {
    name: 'Fan Management',
    primary: 'purple',
    gradient: 'from-purple-600 to-pink-600',
    background: 'bg-slate-50',
    card: 'bg-white',
    text: 'text-slate-900',
    accent: 'text-purple-600',
    icon: MdLibraryMusic
  }
};

const AdminQRScannerPage = () => {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scannedData, setScannedData] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);

  // Determine theme based on activity type
  const theme = THEMES[activityData?.type] || THEMES.event;
  const ThemeIcon = theme.icon;

  useEffect(() => {
    const fetchActivityData = async () => {
      try {
        setLoading(true);
        const response = await getActivityDetails(activityId);
        
        if (!response.success) {
          throw new Error(response.error || 'Failed to fetch activity data');
        }
        
        setActivityData(response.data);
      } catch (err) {
        console.error('Error fetching activity data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityData();
  }, [activityId]);

  const handleScanSuccess = (registrationData) => {
    console.log('Scan successful:', registrationData);
    setScannedData(registrationData);
    setShowResult(true);

    // Add to scan history with WIB timestamp
    const scanRecord = {
      ...registrationData,
      id: Date.now(),
      scannedAt: getCurrentWIB().toISOString(),
    };
    setScanHistory(prev => [scanRecord, ...prev.slice(0, 9)]); // Keep last 10 scans
  };

  const handleScanError = (errorMessage) => {
    console.error('Scan error:', errorMessage);
    setError(errorMessage);
    setTimeout(() => setError(null), 3000);
  };

  const handleBackToScanner = () => {
    setShowResult(false);
    setScannedData(null);
  };

  const handleBackToAdmin = () => {
    navigate(`/admin/${activityId}`);
  };

  const clearScanHistory = () => {
    setScanHistory([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading scanner...</p>
        </div>
      </div>
    );
  }

  if (error && !scannedData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center border border-red-200">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdQrCodeScanner className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2 text-red-600">Scanner Error</h3>
          <p className="mb-6 text-gray-600">{error}</p>
          <button
            onClick={handleBackToAdmin}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
          >
            <MdArrowBack className="mr-2" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">

        {!showResult ? (
          <>
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${theme.gradient} flex items-center justify-center`}>
                    <MdQrCodeScanner className="text-white text-xl" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">QR Code Scanner</h1>
                    <p className="text-gray-600">Scan registration QR codes to verify attendees</p>
                  </div>
                </div>

                <button
                  onClick={handleBackToAdmin}
                  className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  <MdArrowBack className="mr-2" />
                  Back to Dashboard
                </button>
              </div>

              {activityData && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <ThemeIcon className={theme.accent} />
                    <div>
                      <h3 className="font-medium text-gray-900">{activityData.name}</h3>
                      <p className="text-sm text-gray-600">{theme.name}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* QR Scanner */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Scanner</h2>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Ready to scan</span>
                  </div>
                </div>

                <QRScanner
                  onScanSuccess={handleScanSuccess}
                  onScanError={handleScanError}
                  theme={theme}
                />

                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Instructions:</strong> Position the QR code within the scanning area.
                    The scanner will automatically detect and process valid registration codes.
                  </p>
                </div>
              </div>

              {/* Scan History */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <MdHistory className="text-gray-400" />
                    <h2 className="text-lg font-semibold text-gray-900">Recent Scans</h2>
                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                      {scanHistory.length}
                    </span>
                  </div>

                  {scanHistory.length > 0 && (
                    <button
                      onClick={clearScanHistory}
                      className="text-sm text-gray-500 hover:text-red-600 transition-colors duration-200"
                    >
                      Clear History
                    </button>
                  )}
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {scanHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MdQrCodeScanner className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No scans yet</p>
                      <p className="text-sm">Scanned registrations will appear here</p>
                    </div>
                  ) : (
                    scanHistory.map((scan) => (
                      <div key={scan.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${theme.gradient} flex items-center justify-center text-white font-semibold text-sm`}>
                              {(scan.user?.name || scan.name)?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">
                                {scan.user?.name || scan.name || 'Unknown'}
                              </span>
                              <p className="text-sm text-gray-500">
                                {scan.user?.email || scan.email || 'No email'}
                              </p>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${scan.isVerified
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {scan.isVerified ? (
                              <>
                                <MdVerified className="mr-1" />
                                Verified
                              </>
                            ) : (
                              <>
                                <MdPending className="mr-1" />
                                Pending
                              </>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <MdAccessTime />
                          <span>{formatWIBTime(scan.scannedAt)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Scan Result Display */
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MdCheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-gray-900">
                  Registration Verified
                </h2>
                <p className="text-gray-600">
                  Attendee successfully verified for the activity
                </p>
              </div>

              {/* Registration Details */}
              {scannedData && (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">
                    Registration Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <MdPerson className="text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Name</p>
                        <p className="text-gray-900">{scannedData.user?.name || scannedData.name || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <MdEmail className="text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Email</p>
                        <p className="text-gray-900">{scannedData.user?.email || scannedData.email || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <MdPhone className="text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Phone</p>
                        <p className="text-gray-900">{scannedData.user?.phoneNumber || scannedData.phoneNumber || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="text-gray-400">
                        {scannedData.isVerified ? <MdVerified /> : <MdPending />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Status</p>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${scannedData.isVerified
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {scannedData.isVerified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="text-gray-400">#</div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Registration ID</p>
                        <p className="text-gray-900 font-mono text-sm">{scannedData.id || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <MdAccessTime className="text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Registered</p>
                        <p className="text-gray-900">{formatWIBDate(scannedData.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleBackToScanner}
                  className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  <MdRefresh className="mr-2" />
                  Scan Another
                </button>
                <button
                  onClick={handleBackToAdmin}
                  className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  <MdArrowBack className="mr-2" />
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQRScannerPage;